import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Calendar,
  Clock,
  MapPin,
  CreditCard,
  QrCode,
  CheckCircle,
  ArrowLeft,
  AlertCircle,
  Image as ImageIcon,
  Loader2,
  User,
} from 'lucide-react';
import { Button, useToast, Input, Label } from '@caruso/ui';
import { venueReservationCheckoutService } from '@/services/venue-reservation-checkout.service';
import { CreditCardData, CreditCardHolderInfo } from '@/services/payments.service';
import { PixPayment } from '@/components/payment/pix-payment';
import { CreditCardPayment } from '@/components/payment/credit-card-payment';

type PaymentMethod = 'pix' | 'credit_card' | null;

// CPF/CNPJ mask function
function formatCpfCnpj(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 11) {
    // CPF: 000.000.000-00
    return cleaned
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  } else {
    // CNPJ: 00.000.000/0000-00
    return cleaned
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }
}

// Validate CPF
function isValidCpf(cpf: string): boolean {
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit > 9) digit = 0;
  if (digit !== parseInt(cleaned.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit > 9) digit = 0;
  if (digit !== parseInt(cleaned.charAt(10))) return false;

  return true;
}

// Validate CNPJ
function isValidCnpj(cnpj: string): boolean {
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned.charAt(i)) * weights1[i];
  }
  let digit = 11 - (sum % 11);
  if (digit > 9) digit = 0;
  if (digit !== parseInt(cleaned.charAt(12))) return false;

  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned.charAt(i)) * weights2[i];
  }
  digit = 11 - (sum % 11);
  if (digit > 9) digit = 0;
  if (digit !== parseInt(cleaned.charAt(13))) return false;

  return true;
}

function isValidDocument(doc: string): boolean {
  const cleaned = doc.replace(/\D/g, '');
  if (cleaned.length === 11) return isValidCpf(cleaned);
  if (cleaned.length === 14) return isValidCnpj(cleaned);
  return false;
}

function VenueImage({ src, alt }: { src?: string; alt: string }) {
  const [imageError, setImageError] = useState(false);
  const hasImage = src && !imageError;

  return (
    <div className="w-full h-48 rounded-lg overflow-hidden bg-brand-smoke/20 border border-brand-gold/20 flex items-center justify-center">
      {hasImage ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <ImageIcon className="w-16 h-16 text-brand-gold/40" />
      )}
    </div>
  );
}

export function ReservationCheckoutPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [pixData, setPixData] = useState<any>(null);
  const [isPaid, setIsPaid] = useState(false);

  // CPF collection state
  const [showCpfCollection, setShowCpfCollection] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState<PaymentMethod>(null);
  const [cpfInput, setCpfInput] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [customerDocument, setCustomerDocument] = useState<string | null>(null);

  // Fetch reservation data
  const {
    data: checkoutData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['reservation-checkout', id],
    queryFn: () => venueReservationCheckoutService.getCheckoutData(id!),
    enabled: !!id,
    refetchInterval: pixData && !isPaid ? 5000 : false, // Poll for payment status when PIX is active
  });

  // Check if payment was completed
  useEffect(() => {
    if (checkoutData?.isPaid) {
      setIsPaid(true);
    }
  }, [checkoutData]);

  // Set existing PIX data if available
  useEffect(() => {
    if (checkoutData?.pix) {
      setPixData(checkoutData.pix);
      setPaymentMethod('pix');
    }
  }, [checkoutData?.pix]);

  // Track customer document from checkout data
  useEffect(() => {
    if (checkoutData?.reservation?.customerDocument) {
      setCustomerDocument(checkoutData.reservation.customerDocument);
    }
  }, [checkoutData?.reservation?.customerDocument]);

  // Create PIX payment mutation
  const createPixPaymentMutation = useMutation({
    mutationFn: () => venueReservationCheckoutService.createPixPayment(id!),
    onSuccess: (data) => {
      setPixData(data.pix);
      toast({
        title: 'PIX gerado com sucesso!',
        description: 'Escaneie o QR Code ou copie o codigo para pagar',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Erro ao gerar PIX';
      toast({
        title: 'Erro ao gerar PIX',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Create credit card payment mutation
  const createCreditCardPaymentMutation = useMutation({
    mutationFn: async ({
      creditCard,
      holderInfo,
      installments,
    }: {
      creditCard: CreditCardData;
      holderInfo: CreditCardHolderInfo;
      installments: number;
    }) => {
      return venueReservationCheckoutService.createCreditCardPayment(
        id!,
        creditCard,
        holderInfo,
        installments
      );
    },
    onSuccess: (data) => {
      if (data.reservation.paymentStatus === 'paid') {
        setIsPaid(true);
        toast({
          title: 'Pagamento confirmado!',
          description: 'Sua reserva foi confirmada com sucesso',
          variant: 'success',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao processar pagamento',
        description: error.response?.data?.message || 'Verifique os dados do cartao',
        variant: 'destructive',
      });
    },
  });

  // Update customer document mutation
  const updateCustomerDocumentMutation = useMutation({
    mutationFn: (document: string) =>
      venueReservationCheckoutService.updateCustomerDocument(id!, document),
    onSuccess: (data) => {
      setCustomerDocument(data.document);
      setShowCpfCollection(false);
      setCpfInput('');
      setCpfError('');
      toast({
        title: 'CPF salvo com sucesso!',
        description: 'Seu documento foi atualizado',
        variant: 'success',
      });
      // Continue with the pending payment method
      if (pendingPaymentMethod) {
        proceedWithPayment(pendingPaymentMethod);
        setPendingPaymentMethod(null);
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Erro ao salvar CPF';
      setCpfError(errorMessage);
      toast({
        title: 'Erro ao salvar CPF',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Helper function to proceed with payment after CPF is collected
  const proceedWithPayment = (method: PaymentMethod) => {
    setPaymentMethod(method);

    if (method === 'pix' && !pixData) {
      createPixPaymentMutation.mutate();
    }
  };

  const handlePaymentMethodSelect = (method: PaymentMethod) => {
    // Check if customer has a document
    if (!customerDocument) {
      // Show CPF collection screen
      setPendingPaymentMethod(method);
      setShowCpfCollection(true);
      return;
    }

    // Proceed with payment
    proceedWithPayment(method);
  };

  const handleCpfSubmit = () => {
    setCpfError('');

    const cleanedCpf = cpfInput.replace(/\D/g, '');

    if (!cleanedCpf) {
      setCpfError('Por favor, informe seu CPF ou CNPJ');
      return;
    }

    if (!isValidDocument(cpfInput)) {
      setCpfError('CPF ou CNPJ invalido');
      return;
    }

    updateCustomerDocumentMutation.mutate(cleanedCpf);
  };

  const handleCpfCancel = () => {
    setShowCpfCollection(false);
    setPendingPaymentMethod(null);
    setCpfInput('');
    setCpfError('');
  };

  const handleCreditCardSubmit = async (
    creditCard: CreditCardData,
    holderInfo: CreditCardHolderInfo,
    installments: number
  ) => {
    await createCreditCardPaymentMutation.mutateAsync({
      creditCard,
      holderInfo,
      installments,
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price || 0);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-brand-ink to-brand-smoke flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-brand-gold animate-spin mx-auto mb-4" />
          <p className="text-brand-ivory">Carregando dados da reserva...</p>
        </div>
      </div>
    );
  }

  if (error || !checkoutData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-brand-ink to-brand-smoke flex items-center justify-center p-4">
        <div className="bg-brand-ink/50 border border-red-500/30 rounded-lg p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-brand-gold mb-2">Reserva nao encontrada</h1>
          <p className="text-brand-ivory/60 mb-6">
            Nao foi possivel encontrar a reserva solicitada. Verifique se o link esta correto.
          </p>
          <Button onClick={() => navigate('/')} variant="outline">
            Voltar ao inicio
          </Button>
        </div>
      </div>
    );
  }

  const { reservation } = checkoutData;

  // Payment success screen
  if (isPaid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-brand-ink to-brand-smoke flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-8 max-w-md text-center"
        >
          <div className="bg-green-500/20 border-2 border-green-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h1 className="text-3xl font-serif text-brand-gold mb-4">
            Pagamento Confirmado!
          </h1>
          <p className="text-brand-ivory/80 mb-6">
            Sua reserva no <strong className="text-brand-gold">{reservation.venue.name}</strong> foi confirmada com sucesso.
          </p>
          <div className="bg-brand-smoke/50 border border-brand-gold/20 rounded-lg p-4 mb-6 text-left">
            <div className="space-y-2 text-sm text-brand-ivory">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-brand-gold" />
                <span>{reservation.startDateFormatted}</span>
              </div>
              {reservation.startTime && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-brand-gold" />
                  <span>{reservation.startTime} - {reservation.endTime}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-brand-gold" />
                <span>{reservation.venue.name}</span>
              </div>
            </div>
          </div>
          <p className="text-brand-ivory/60 text-sm">
            Voce recebera um email com os detalhes da sua reserva.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-ink to-brand-smoke">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif text-brand-gold mb-2">
            Pagamento da Reserva
          </h1>
          <p className="text-brand-ivory/60">
            Complete o pagamento para confirmar sua reserva
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Reservation Summary */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg overflow-hidden"
          >
            {/* Venue Image */}
            <VenueImage src={reservation.venue.image} alt={reservation.venue.name} />

            <div className="p-6 space-y-4">
              <h2 className="text-2xl font-serif text-brand-gold">
                {reservation.venue.name}
              </h2>

              {reservation.venue.description && (
                <p className="text-brand-ivory/60 text-sm line-clamp-2">
                  {reservation.venue.description}
                </p>
              )}

              {/* Reservation Details */}
              <div className="border-t border-brand-gold/20 pt-4 space-y-3">
                <div className="flex items-center gap-3 text-brand-ivory">
                  <Calendar className="w-5 h-5 text-brand-gold" />
                  <div>
                    <p className="text-sm text-brand-ivory/60">Data</p>
                    <p className="font-medium">{reservation.startDateFormatted}</p>
                  </div>
                </div>

                {reservation.startTime && (
                  <div className="flex items-center gap-3 text-brand-ivory">
                    <Clock className="w-5 h-5 text-brand-gold" />
                    <div>
                      <p className="text-sm text-brand-ivory/60">Horario</p>
                      <p className="font-medium">{reservation.startTime} - {reservation.endTime}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 text-brand-ivory">
                  <MapPin className="w-5 h-5 text-brand-gold" />
                  <div>
                    <p className="text-sm text-brand-ivory/60">Cliente</p>
                    <p className="font-medium">{reservation.customerName}</p>
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-brand-gold/20 pt-4 space-y-2">
                <div className="flex justify-between text-brand-ivory/60 text-sm">
                  <span>Valor base</span>
                  <span>{formatPrice(reservation.basePrice)}</span>
                </div>
                {reservation.discount > 0 && (
                  <div className="flex justify-between text-green-500 text-sm">
                    <span>Desconto</span>
                    <span>-{formatPrice(reservation.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-brand-gold text-xl font-bold pt-2 border-t border-brand-gold/20">
                  <span>Total</span>
                  <span>{formatPrice(reservation.total)}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Payment Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* CPF Collection Screen */}
            {showCpfCollection && !paymentMethod && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6"
              >
                <Button
                  variant="ghost"
                  onClick={handleCpfCancel}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>

                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-brand-gold/10 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-brand-gold" />
                  </div>
                  <div>
                    <h2 className="text-xl font-serif text-brand-gold">
                      Informe seu CPF
                    </h2>
                    <p className="text-brand-ivory/60 text-sm">
                      Necessario para processar o pagamento
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="cpf" className="text-brand-ivory/80 text-sm">
                      CPF ou CNPJ
                    </Label>
                    <Input
                      id="cpf"
                      type="text"
                      value={cpfInput}
                      onChange={(e) => {
                        setCpfError('');
                        setCpfInput(formatCpfCnpj(e.target.value));
                      }}
                      placeholder="000.000.000-00"
                      className="mt-1 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                      maxLength={18}
                    />
                    {cpfError && (
                      <p className="mt-1 text-sm text-red-500">{cpfError}</p>
                    )}
                  </div>

                  <p className="text-xs text-brand-ivory/50">
                    Seu CPF sera salvo no seu cadastro para facilitar compras futuras.
                  </p>

                  <Button
                    onClick={handleCpfSubmit}
                    disabled={updateCustomerDocumentMutation.isPending}
                    className="w-full bg-brand-gold hover:bg-brand-gold/90 text-brand-ink font-semibold"
                  >
                    {updateCustomerDocumentMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      'Continuar para pagamento'
                    )}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Payment Method Selection */}
            {!paymentMethod && !showCpfCollection && (
              <div className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6">
                <h2 className="text-xl font-serif text-brand-gold mb-4">
                  Escolha a forma de pagamento
                </h2>

                <div className="grid grid-cols-1 gap-4">
                  <button
                    onClick={() => handlePaymentMethodSelect('pix')}
                    disabled={createPixPaymentMutation.isPending}
                    className="bg-brand-smoke/30 border-2 border-brand-gold/20 hover:border-brand-gold rounded-lg p-6 transition-all hover:scale-[1.02] flex items-center gap-4"
                  >
                    <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center">
                      <QrCode className="w-8 h-8 text-brand-gold" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-xl font-serif text-brand-gold mb-1">PIX</h3>
                      <p className="text-brand-ivory/60 text-sm">
                        {createPixPaymentMutation.isPending ? 'Gerando PIX...' : 'Pagamento instantaneo'}
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={() => handlePaymentMethodSelect('credit_card')}
                    className="bg-brand-smoke/30 border-2 border-brand-gold/20 hover:border-brand-gold rounded-lg p-6 transition-all hover:scale-[1.02] flex items-center gap-4"
                  >
                    <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center">
                      <CreditCard className="w-8 h-8 text-brand-gold" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-xl font-serif text-brand-gold mb-1">Cartao de Credito</h3>
                      <p className="text-brand-ivory/60 text-sm">
                        Parcelamento em ate 12x
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* PIX Payment */}
            {paymentMethod === 'pix' && pixData && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6"
              >
                <Button
                  variant="ghost"
                  onClick={() => {
                    setPaymentMethod(null);
                  }}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <PixPayment pixData={pixData} />
              </motion.div>
            )}

            {/* Credit Card Payment */}
            {paymentMethod === 'credit_card' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6"
              >
                <Button
                  variant="ghost"
                  onClick={() => setPaymentMethod(null)}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <CreditCardPayment
                  onSubmit={handleCreditCardSubmit}
                  totalAmount={reservation.total}
                  isProcessing={createCreditCardPaymentMutation.isPending}
                />
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
